/**
 * Priority-shipping line-item flag — M1-2 of the shipping-events program.
 *
 * Eligible cart lines are stamped with two line-item properties at add-to-cart:
 *
 *   { "_priorityItem": "true", "_priorityPhase": "<phase stamp>" }
 *
 * Order line properties are WRITE-ONCE — no API can correct them after checkout —
 * and the warehouse (Boomi/SAP, via the shipping-events app's `holiday_shipping`
 * order tag) fulfills on this flag without re-deriving eligibility. A wrong write
 * is a wrong promise on a real order, so the semantics here are strict:
 *
 *   - Only the literal string 'true' claims priority. Absent and 'false' both
 *     mean "no promise made" — ineligible lines are left UNTOUCHED rather than
 *     stamped 'false', so when in doubt nothing is claimed.
 *   - Other apps' properties on the same line (_customProductUAID__c etc.) must
 *     never be dropped or rewritten; downstream matches on property NAME.
 *
 * Eligibility (M3-5, since 2026-09-30): a product is eligible NOW when the
 * active phase's key — `${event}:${phaseId}` — is in the product's
 * `lig_shipping_events.eligible_phases` stamp, which the Shipping Events app
 * resolves from each phase's criteria and writes onto the product. The stamp is
 * published PER PRODUCT by components/cart/snippet.priority-item-eligibility.liquid,
 * wherever Liquid renders a product — as `{ phases, variantIds }` entries on
 * window.TMW.priorityEligibleProducts — never by enumerating a collection, which
 * is O(catalog) and fails at real catalog sizes (app repo, D14). The entries are
 * bounded by what the page rendered rather than by the catalog, and which of
 * them is eligible NOW is decided here, in JS, against the promise resolved from
 * the calendar at render or add time (eligibleVariantIdsFor) — never in Liquid,
 * because a cached page's Liquid clock is its render time.
 *
 * Eligibility is a PARAMETER to every contract function here, never read from
 * a global inside them. That is what kept the M3-5 swap contained: the producer
 * of the set changed (the snippet, and the reader below) and nothing in
 * applyPriorityProperties / appendPriorityFormProperties moved. The property
 * names and value semantics here are the permanent contract.
 *
 * ⚠️ GAP: surfaces whose product cards are rendered client-side from Constructor
 * or Nosto data have no Liquid product, so they contribute no entry and their
 * adds go unflagged. That is the safe direction (a missed promise, never a
 * false one) and it closes in M5-6, when Constructor indexes the stamp directly
 * and the card data carries it. See AGENTS.md for the full note.
 *
 * Full contract: the shipping-events app repo, docs/theme-contract.md.
 * Theme-side map: AGENTS.md → "Priority shipping flag at add-to-cart (M1-2)".
 */

export const PRIORITY_ITEM_PROPERTY = '_priorityItem';
export const PRIORITY_PHASE_PROPERTY = '_priorityPhase';
export const PRIORITY_ITEM_VALUE = 'true';

/**
 * The opaque staleness stamp naming which phase made the promise, resolved from
 * the live storefront calendar AT ADD TIME (this replaced the `'m1-hardcoded'`
 * M1 placeholder on 2026-09-17). Callers pass activeShippingPromise's result;
 * kept as a plain-object formatter here (never importing shipping-events) so
 * the wire contract stays in one dependency-free file and the import direction
 * stays one-way.
 *
 * Format: `${event}:${phaseId}` — e.g. `halloween-2026:1a` — the same alphabet
 * as the product eligibility stamp, and NEVER a bare phase id, which collides
 * across years (a 2027 order reading as eligible under 2026's stamp). It is
 * always a SINGLE value: two shipping events may not overlap, enforced at save
 * time in the app (D14). Downstream treats it as an opaque string; line
 * properties are write-once, so this format is permanent.
 *
 * No active promise — out of window, an in-window gap, a disabled event, or a
 * degenerate promise missing either half — resolves to null, and the writers
 * below then write NO `_priorityPhase` at all: an eligible line added outside
 * any event still flags for the warehouse but claims no phase, exactly as the
 * messaging renders nothing then. A stamp at add time is the point — M5-3 owns
 * re-asserting a cart that crossed a boundary.
 *
 * @param {{event: string|null, phaseId: string|null}|null|undefined} promise
 * @returns {string|null}
 */
export const priorityPhaseStamp = (promise) =>
  promise?.event && promise?.phaseId ? `${promise.event}:${promise.phaseId}` : null;
/** Key under window.TMW where the eligibility snippet publishes the per-product entries. */
export const PRIORITY_ELIGIBILITY_GLOBAL = 'priorityEligibleProducts';

/**
 * Normalize any variant-id shape the theme's add paths produce — Storefront GID
 * ('gid://shopify/ProductVariant/123'), number, or numeric string — to a plain
 * numeric string, or null when there is no usable id.
 */
const normalizeVariantId = (variantId) => {
  if (variantId === null || variantId === undefined) return null;
  const raw = String(variantId);
  const numeric = raw.includes('gid://') ? raw.split('/').slice(-1)[0] : raw;
  return /^\d+$/.test(numeric) ? numeric : null;
};

/**
 * Read the per-product entries the Liquid snippet published — one
 * `{ phases: ['halloween-2026:1a', …], variantIds: [111, …] }` per rendered
 * product that carries a `lig_shipping_events.eligible_phases` stamp. Kept as a
 * function taking `win` (rather than reading the global directly) so callers
 * stay unit-testable and the TMW key literal lives in exactly one file.
 * @param {object} win — window (or a stand-in under test)
 * @returns {Array<{phases: Array<string>, variantIds: Array<number|string>}>|undefined}
 *   the entries, or undefined when the layout initializer never rendered
 */
export const readPriorityEligibleProducts = (win) => win?.TMW?.[PRIORITY_ELIGIBILITY_GLOBAL];

/**
 * The variant ids eligible RIGHT NOW: every rendered product whose stamp holds
 * the active promise's `${event}:${phaseId}` key, flattened to its variant ids.
 * This is the M3-5 rule — "eligible now?" is `active phase key ∈ stamp` —
 * answered in JS at the moment it is asked (render, or add), never in Liquid,
 * because the calendar's boundaries may only be compared against a live clock.
 * The key is built by priorityPhaseStamp, so the stamp written onto the line
 * and the stamp matched on the product are the same alphabet by construction.
 *
 * Three outcomes, and the difference between the first two is load-bearing:
 *   - `undefined` — `entries` is not an array: the layout initializer never
 *     rendered, the mechanism itself is gone. The PDP renders NOTHING in that
 *     case (pdpShippingEventMessage) rather than telling every product,
 *     eligible ones included, that it misses the event.
 *   - `[]` — wired, but nothing on this page is eligible now: no active promise
 *     (out of window, an in-window gap, a disabled event), or no rendered
 *     product carries the active key. A stamp for a phase that is not active —
 *     last year's event, a phase that has ended — is inert here.
 *   - ids — the eligible set the contract functions below take as a parameter.
 * Every malformed entry (no `phases` array, no `variantIds` array) contributes
 * nothing: the failure direction that never claims a false promise. Whole-key
 * equality only — `halloween-2026:1` must never match `halloween-2026:1a`.
 * @param {Array<object>|undefined} entries — readPriorityEligibleProducts' result
 * @param {{event: string|null, phaseId: string|null}|null|undefined} promise — activeShippingPromise's result
 * @returns {Array<number|string>|undefined}
 */
export const eligibleVariantIdsFor = (entries, promise) => {
  if (!Array.isArray(entries)) return undefined;
  const key = priorityPhaseStamp(promise);
  if (!key) return [];
  return entries
    .filter((entry) => Array.isArray(entry?.phases) && entry.phases.includes(key))
    .flatMap((entry) => (Array.isArray(entry?.variantIds) ? entry.variantIds : []));
};

/**
 * The eligible variant-id set for the active promise, read off the page — the
 * one call every consumer makes (both add paths and the PDP render), so the
 * flag and the message can never disagree about a product. Resolve the promise
 * ONCE per add or render and pass the same object here and to
 * priorityPhaseStamp, so the set and the line's stamp describe one instant.
 * @param {object} win — window (or a stand-in under test)
 * @param {{event: string|null, phaseId: string|null}|null|undefined} promise — activeShippingPromise's result
 * @returns {Array<number|string>|undefined} the set, or undefined when the mechanism is absent
 */
export const readPriorityEligibleVariantIds = (win, promise) =>
  eligibleVariantIdsFor(readPriorityEligibleProducts(win), promise);

/**
 * True only when the variant id is present, parseable, and in the eligibility
 * set. Every degenerate input (missing set, empty set, malformed id) is
 * ineligible — the failure direction that never claims a false promise.
 * @param {string|number|null|undefined} variantId — GID, number, or numeric string
 * @param {Array<number|string>|undefined} eligibleVariantIds — the snippet's set
 */
export const isPriorityEligible = (variantId, eligibleVariantIds) => {
  if (!Array.isArray(eligibleVariantIds) || eligibleVariantIds.length === 0) return false;
  const normalized = normalizeVariantId(variantId);
  if (!normalized) return false;
  return eligibleVariantIds.some((id) => String(id) === normalized);
};

/**
 * Stamp the priority properties onto a line's `properties` map when the
 * variant is eligible; return the map untouched (same reference) when not.
 * Never removes or rewrites any other key.
 *
 * The phase stamp is a PARAMETER for the same reason eligibility is — the
 * producer (the add path, at add time) resolves it, so nothing here reads the
 * document or the clock. A null/absent stamp writes `_priorityItem` alone;
 * this function does not know how the set was produced. In practice, since
 * the M3-5 swap derives the set from the same promise, no active promise means
 * an EMPTY set, so both properties are written or neither is.
 * @param {Object<string,string>} properties — the line's properties so far
 * @param {string|number|null|undefined} variantId
 * @param {Array<number|string>|undefined} eligibleVariantIds
 * @param {string|null|undefined} phaseStamp — priorityPhaseStamp(activeShippingPromise(…)), resolved at add time
 * @returns {Object<string,string>}
 */
export const applyPriorityProperties = (properties, variantId, eligibleVariantIds, phaseStamp) => {
  if (!isPriorityEligible(variantId, eligibleVariantIds)) return properties;
  return {
    ...properties,
    [PRIORITY_ITEM_PROPERTY]: PRIORITY_ITEM_VALUE,
    ...(phaseStamp ? { [PRIORITY_PHASE_PROPERTY]: phaseStamp } : {}),
  };
};

/**
 * Form-encoded twin of applyPriorityProperties for add paths that POST a form
 * (properties travel as `properties[...]` fields). Appends the flag — and the
 * phase stamp when one resolved — when the variant is eligible and the form
 * doesn't already carry the flag; no-op otherwise.
 * @param {FormData} formData — mutated in place
 * @param {string|number|null|undefined} variantId — usually formData.get('id')
 * @param {Array<number|string>|undefined} eligibleVariantIds
 * @param {string|null|undefined} phaseStamp — priorityPhaseStamp(activeShippingPromise(…)), resolved at add time
 */
export const appendPriorityFormProperties = (formData, variantId, eligibleVariantIds, phaseStamp) => {
  const itemField = `properties[${PRIORITY_ITEM_PROPERTY}]`;
  if (formData.has(itemField) || !isPriorityEligible(variantId, eligibleVariantIds)) return;
  formData.append(itemField, PRIORITY_ITEM_VALUE);
  if (phaseStamp) formData.append(`properties[${PRIORITY_PHASE_PROPERTY}]`, phaseStamp);
};
